import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Chip, TextField } from "@mui/material";
import moment from "moment";
import { z } from "zod";

import { FormColumns } from "components/form/FormColumns/FormColumns";
import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { DatePickerField } from "components/ui/DatePickerField";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import patientAutocompleteStyles from "components/ui/PatientAutocomplete/PatientAutocomplete.module.css";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea";
import { useCreateTask } from "features/comunications/hooks/useCreateTask";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { TaskCategory, TaskPriority, Taskstatus } from "utils/Constant";
import { API_DATE_TIME_FORMAT } from "utils/dateFormatter";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./AddTaskForm.module.css";

const buildPatientOption = (patientData) => ({
  id: patientData.id,
  title: `${patientData.firstName}${
    patientData.surname ? " " + patientData.surname : ""
  }${
    patientData.dateOfBirth
      ? ` (${moment(patientData.dateOfBirth).format("DD-MM-YYYY")})`
      : ""
  }`,
});

const formatStaffLabel = (staffMember) =>
  `${staffMember.firstName} ${staffMember.lastName}${
    staffMember.staffRole ? ` (${staffMember.staffRole.toLowerCase()})` : ""
  }`;

const StaffPresenceDot = ({ isOnline }) => (
  <span
    className={`${style.presenceDot} ${
      isOnline ? style.presenceDotOnline : style.presenceDotOffline
    }`}
    title={isOnline ? "Online" : "Offline"}
    aria-label={isOnline ? "Online" : "Offline"}
  />
);

const formSchema = z.object({
  patientId: z.number().optional(),
  assigneeIds: z
    .array(z.string())
    .min(1, { message: "Please assign this task to a staff member." }),
  from: z.string().min(1, { message: "Please select from." }),
  priority: z.string().min(1, { message: "Please select priority." }),
  status: z.string().min(1, { message: "Please select status." }),
  taskCategory: z.string().min(1, { message: "Please select task type." }),
  dueDate: z.string().min(1, { message: "Please select due date." }),
  patientName: z.string().optional(),
  assigneeComments: z.string().optional(),
  taskDescription: z
    .string()
    .min(1, { message: "Please provide task description." }),
});

export const AddTaskForm = ({
  patient,
  task,
  closeModal,
  staffMemberId,
  initialAssigneeComments,
  initialTaskDescription,
  compact = false,
  formId,
  noDefaultAssignee = false,
}) => {
  let defaultPatient = {};

  if (patient) defaultPatient = buildPatientOption(patient);
  if (task?.patient) defaultPatient = buildPatientOption(task.patient);

  const [selectedPatient, setSelectedPatient] = useState(defaultPatient);

  const { data: staffMembers } = useStaffMembers();
  const { data: staffInfo } = useStaffInfo();
  const saveTask = useCreateTask();

  const assignableStaff = (staffMembers || []).filter(
    (s) => !s.disabled && s.showOnTeam
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      patientId: task?.patientId || patient?.id || undefined,
      assigneeIds:
        task?.assigneeIds?.map(String) ||
        (staffMemberId ? [String(staffMemberId)] : []),
      from: localStorage.getItem("kinesin-name"),
      priority:
        task?.priority?.toString() || TaskPriority.at(2).value.toString(),
      status: task?.status?.toString() || Taskstatus.at(0).value.toString(),
      taskCategory: task?.taskCategory || TaskCategory.at(0).value,
      dueDate: task?.dueDate
        ? moment(task?.dueDate, "YYYY-MM-DD HH:mm").format("DD-MM-YYYY")
        : moment(new Date()).format("DD-MM-YYYY"),
      taskDescription: task?.title || initialTaskDescription || "",
      assigneeComments: task?.assigneeComments || initialAssigneeComments || "",
    },
  });

  useEffect(() => {
    if (
      !task &&
      !staffMemberId &&
      !noDefaultAssignee &&
      form.getValues("assigneeIds").length === 0 &&
      staffInfo?.staffMember?.id
    ) {
      form.setValue("assigneeIds", [String(staffInfo.staffMember.id)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffInfo, noDefaultAssignee]);

  const handleFormSubmit = async (values) => {
    await saveTask.mutate(
      {
        id: task?.id,
        // ISO 8601 with seconds (KIN-2181).
        dueDate: moment(values.dueDate, "DD-MM-YYYY").format(
          API_DATE_TIME_FORMAT
        ),
        patientId: values.patientId,
        assigneeIds: values.assigneeIds.map(Number),
        assignedGroup: null,
        priority: Number(values.priority),
        status: values.status,
        taskCategory: values.taskCategory,
        title: values.taskDescription,
        assigneeComments: values.assigneeComments,
        tenant: localStorage.getItem("kinesin-tenant"),
      },
      {
        onSuccess: () => {
          closeModal?.(false);
        },
      }
    );
  };

  const columnCount = compact ? 1 : 2;
  const columnGap = compact ? 12 : 24;
  const textareaRows = compact ? 3 : 6;

  return (
    <FormProvider {...form}>
      <form
        id={formId}
        className={style.addTaskForm}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddTaskForm"))}
        style={compact ? { gap: "8px" } : undefined}
      >
        <FormColumns columns={columnCount} gap={columnGap}>
          <FormField name="patientId" label="Patient">
            <PatientAutocomplete
              id="task-patient-search"
              value={selectedPatient}
              onChange={(newValue) => {
                setSelectedPatient(newValue);
                form.setValue("patientId", newValue?.id);
              }}
              popperZIndex={compact ? 10001 : undefined}
            />
          </FormField>
          <FormField name="assigneeIds" label="Assign To">
            <div className={style.assignmentField}>
              <Autocomplete
                multiple
                className={patientAutocompleteStyles.autocomplete}
                options={assignableStaff}
                getOptionLabel={formatStaffLabel}
                renderOption={(props, staffMember) => (
                  <li {...props} key={staffMember.id}>
                    <span className={style.staffOption}>
                      <StaffPresenceDot isOnline={!!staffMember.active} />
                      {formatStaffLabel(staffMember)}
                    </span>
                  </li>
                )}
                renderTags={(selectedStaff, getTagProps) =>
                  selectedStaff.map((staffMember, index) => {
                    const { key, ...tagProps } = getTagProps({ index });

                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={
                          <span className={style.staffOption}>
                            <StaffPresenceDot
                              isOnline={!!staffMember.active}
                            />
                            {formatStaffLabel(staffMember)}
                          </span>
                        }
                      />
                    );
                  })
                }
                isOptionEqualToValue={(opt, val) =>
                  String(opt.id) === String(val?.id ?? val)
                }
                value={assignableStaff.filter((s) =>
                  (form.watch("assigneeIds") || []).includes(String(s.id))
                )}
                onChange={(_, newValue) =>
                  form.setValue(
                    "assigneeIds",
                    newValue.map((s) => String(s.id))
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search staff..." />
                )}
                disablePortal={false}
                componentsProps={{
                  popper: {
                    style: { zIndex: compact ? 10001 : 1500 },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: { boundary: "viewport" },
                      },
                    ],
                  },
                  paper: {
                    sx: {
                      fontFamily: "Nunito, sans-serif",
                      fontSize: "14px",
                      borderRadius: "12px",
                      marginTop: "4px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
                slotProps={{
                  popper: {
                    style: { zIndex: compact ? 10001 : 1500 },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: { boundary: "viewport" },
                      },
                    ],
                  },
                  paper: {
                    sx: {
                      fontFamily: "Nunito, sans-serif",
                      fontSize: "14px",
                      borderRadius: "12px",
                      marginTop: "4px",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
              />
              {!!form.formState.errors.assigneeIds && (
                <span className={style.assignmentError}>
                  {form.formState.errors.assigneeIds.message}
                </span>
              )}
            </div>
          </FormField>
        </FormColumns>
        <FormColumns columns={columnCount} gap={columnGap}>
          {!compact && (
            <FormField name="from" label="From">
              <CustomInput {...form.register("from")} disabled />
            </FormField>
          )}
          <FormField name="priority" label="Priority">
            <Controller
              name="priority"
              control={form.control}
              render={({ field }) => (
                <Select
                  name="priority"
                  options={TaskPriority.map((priority) => ({
                    label: priority.name,
                    value: String(priority.value),
                  }))}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={null}
                  fullWidth
                />
              )}
            />
          </FormField>
        </FormColumns>
        <FormColumns columns={columnCount} gap={columnGap}>
          <FormField name="taskCategory" label="Task Type">
            <Controller
              name="taskCategory"
              control={form.control}
              render={({ field }) => (
                <Select
                  name="taskCategory"
                  options={TaskCategory.map((category) => ({
                    label: category.name,
                    value: category.value,
                  }))}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={null}
                  fullWidth
                />
              )}
            />
          </FormField>
          <FormField name="status" label="Status">
            <Controller
              name="status"
              control={form.control}
              render={({ field }) => (
                <Select
                  name="status"
                  options={Taskstatus.map((status) => ({
                    label: status.name,
                    value: String(status.value),
                  }))}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={null}
                  fullWidth
                />
              )}
            />
          </FormField>
        </FormColumns>
        <FormColumns columns={columnCount} gap={columnGap}>
          <FormField name="dueDate" label="Due Date">
            <Controller
              name="dueDate"
              control={form.control}
              render={({ field }) => (
                <DatePickerField
                  value={moment(field.value, "DD-MM-YYYY").format("YYYY-MM-DD")}
                  onChange={(date) =>
                    field.onChange(
                      moment(date, "YYYY-MM-DD").format("DD-MM-YYYY")
                    )
                  }
                />
              )}
            />
          </FormField>
        </FormColumns>
        <FormField name="taskDescription" label="Instruction">
          <Textarea rows={textareaRows} {...form.register("taskDescription")} />
        </FormField>
        <FormField name="assigneeComments" label="Assignee Notes">
          <Textarea rows={textareaRows} {...form.register("assigneeComments")} />
        </FormField>
        {!compact && (
          <div className={style.bottomSection}>
            <Button
              variant="primary"
              size="large"
              type="submit"
              disabled={saveTask.isPending}
            >
              {saveTask.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
